import { Link } from '../router/Link';
import { Thumbnail, cx } from '../ui';
import { AddToPlaylist } from '../playlists/AddToPlaylist';
import { AddToQueue } from '../queue/AddToQueue';
import { QueueCardAsk } from '../queue/QueueCardAsk';
import { useQueueCardAsk } from '../queue/useQueueCardAsk';
import { useQueueCardPress } from '../queue/useQueueCardPress';
import { WatchLaterButton } from '../playlists/WatchLaterButton';
import { useDeArrowBranding } from '../dearrow/useDeArrowBranding';
import { CardActions } from './CardActions';
import { ThumbnailOverlay } from './ThumbnailOverlay';
import { useGridPreview } from './useGridPreview';
import type { ListingItem } from './model';
import styles from './listing.module.css';
import { useCopy } from '../strings/useCopy';

/**
 * One video in a feed grid. Renders props — every href arrived built and every string arrived
 * formatted (R13); the only decision left is whether a field exists to draw.
 *
 * Three anchors, arranged the way the rail's rows already are and for the same reason: an anchor
 * inside an anchor is invalid HTML that browsers resolve by silently dropping one, so a big mouse
 * target *and* a separate channel link means duplicating the link rather than nesting it. The
 * thumbnail's copy is `aria-hidden` + `tabIndex={-1}`, which leaves exactly one link per card for a
 * keyboard and a screen reader while a mouse still gets the whole picture.
 *
 * `tabIndex` comes from the caller, not from here: §9's roving model means one card in the grid is
 * tabbable and the arrow keys move which (see `useGridFocus`). Both real anchors take the same value,
 * so Tab reaches the focused card's channel link and then leaves the grid.
 */
export interface ListingCardProps {
  readonly item: ListingItem;
  readonly tabIndex: number;
  /** Fires on either anchor, so clicking a card makes it the grid's roving member. */
  readonly onFocus?: () => void;
}

export function ListingCard({ item, tabIndex, onFocus }: ListingCardProps) {
  const words = useCopy().text.listing;
  // DeArrow (2026-08-02): the community's title and thumbnail where they exist, each half
  // falling back to the record's own. `null`s while the integration is off — free by design.
  const dearrow = useDeArrowBranding(item.videoId);
  const title = dearrow.title ?? item.title;
  const thumbnailUrl = dearrow.thumbnailUrl ?? item.thumbnailUrl;
  // The hover preview (2026-08-02): while it shows, its tile covers the image and its bar
  // replaces the watched-progress bar — one bar, whichever statement is currently the truer one.
  /*
   * What a plain press on this card means. Nothing at all until a queue exists, at which point the
   * press is claimed and this card answers on its own picture — `queue/useQueueCardPress.ts` for
   * both halves, including why every modifier the browser owns still goes to the browser.
   */
  const onPress = useQueueCardPress(item);
  const asking = useQueueCardAsk(item);
  const preview = useGridPreview({
    videoId: item.videoId,
    durationSec: item.durationSec,
    // A premiere counts too: it has not been recorded, so its track is a 404 rather than a picture.
    noPreview: item.badge?.kind === 'live' || item.badge?.kind === 'upcoming',
  });
  return (
    <li className={cx(styles.card, item.watched && styles.watched)} onFocus={onFocus}>
      <Link className={styles.thumbLink} href={item.href} tabIndex={-1} ariaHidden onClick={onPress}>
        {/*
         * 4b's progress bar rides on the thumbnail, which is `aria-hidden` here — so it is decoration by
         * construction and the spoken half of it is the marker below (`listing/model.ts`).
         */}
        <Thumbnail
          src={thumbnailUrl}
          badge={item.badge}
          progress={preview.progress ?? item.progress}
          className={styles.thumb}
          onPointerEnter={preview.onPointerEnter}
          onPointerLeave={preview.onPointerLeave}
          onPointerMove={preview.onPointerMove}
          overlay={<ThumbnailOverlay preview={preview} watched={item.watched} />}
        />
      </Link>

      <div className={styles.text}>
        <Link
          className={styles.title}
          href={item.href}
          title={title}
          tabIndex={tabIndex}
          onClick={onPress}
        >
          {/*
           * Spoken, not shown — the visible words are retired: the progress bar on the thumbnail is
           * `aria-hidden` decoration, so this line is the whole of what a screen reader hears about
           * watched state — inside the link's text, so it reads "Watched — <title>" in one go.
           */}
          {item.marker === null ? null : (
            <span className={styles.srOnly}>{item.marker.label} — </span>
          )}
          {title}
        </Link>

        <div className={styles.byline}>
          {item.authorHref === null ? (
            // No link, on purpose: a collaboration video has no single channel to point at.
            <span className={styles.author} title={item.author}>
              {item.author}
            </span>
          ) : (
            <Link
              className={styles.author}
              href={item.authorHref}
              title={item.author}
              tabIndex={tabIndex}
            >
              {item.author}
            </Link>
          )}
          {item.authorVerified ? (
            <span className={styles.verified} title={words.verified}>
              ✓
            </span>
          ) : null}
        </div>

        {/*
         * Both halves are optional and independently so. A feed whose scrape produced no counts and
         * no dates draws neither — an absence, rather than "0 views · now" (`feed/listing.ts`).
         */}
        {item.viewCountText === null && item.publishedText === null ? null : (
          <div className={styles.meta}>
            {item.viewCountText === null ? null : (
              <span title={item.viewCountTitle ?? undefined}>
                {words.meta.views(item.viewCountText)}
              </span>
            )}
            {item.publishedText === null ? null : (
              <span title={item.publishedTitle ?? undefined}>{item.publishedText}</span>
            )}
          </div>
        )}
      </div>

      {/*
       * The Watch later toggle over the frame's corner (2026-08-02), with Save to playlist
       * below it (2026-08-06) — drawn on the picture, written after the text,
       * `listing/CardActions.tsx` for both halves of why.
       */}
      <CardActions covered={asking}>
        <WatchLaterButton
          face="corner"
          videoId={item.videoId}
          videoTitle={title}
          tabIndex={tabIndex}
        />
        <AddToPlaylist
          face="corner"
          videoId={item.videoId}
          videoTitle={title}
          tabIndex={tabIndex}
        />
        {/*
         * Third in the stack, and the only one of the three that renders signed out: a queue belongs
         * to this browser tab, so there is no account for it to need (`queue/AddToQueue.tsx`).
         */}
        <AddToQueue item={item} tabIndex={tabIndex} />
      </CardActions>

      {/*
       * The two answers, over the picture and only on the card that was pressed
       * (`queue/QueueCardAsk.tsx`). A sibling of the thumbnail's link for `CardActions`' reason, and
       * written last so a keyboard reaches the video's title before the buttons that act on it.
       */}
      <QueueCardAsk item={item} tabIndex={tabIndex} />
    </li>
  );
}
